import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import BotonCompartir from './Compartir';
import { imagenExtracto } from './imagenCompartir';
import { extractosDeFecha } from './useExtractos';
import './TableroExtractos.css';

/**
 * Extractos de quiniela de la home comercial.
 *
 * Dos piezas con trabajos distintos:
 *
 *   1. El panel de arriba VENDE. Va rotando entre las cinco loterías del
 *      último turno sorteado, una por vez y en grande. Cada lotería es la
 *      protagonista durante unos segundos.
 *   2. La tabla de abajo SIRVE. Está el día entero, compacta, para el que
 *      viene a buscar un turno anterior y no quiere esperar la rotación.
 *
 * En los dos casos, tocar un número abre los 20 en un modal.
 *
 * Los logos van una sola vez por lotería —en el panel y en la cabecera de la
 * tabla— y no repetidos en cada celda: en una grilla de turnos por loterías,
 * el logo por celda repite cinco veces la misma marca y tapa el dato.
 */

// Los ids no siguen el orden en que se sortea ni en que se muestra, así que
// las dos listas van explícitas. Espejan las del backend (extractos/parser.js).
const ORDEN_TURNOS = [7, 1, 2, 3, 4];
const ORDEN_LOTERIAS = [6, 1, 8, 7, 2];
const LOTERIA_PROPIA = 6; // Jujeña: es la de casa, abre la rotación

const NOMBRE_LOTERIA = {
  6: 'Tómbola Jujeña',
  1: 'Lotería de la Ciudad',
  8: 'Lotería de la Provincia',
  7: 'Lotería de Córdoba',
  2: 'Lotería de Salta',
};

// Nombre corto para la tabla, donde no entra el largo.
const NOMBRE_CORTO = {
  6: 'Jujeña',
  1: 'Ciudad',
  8: 'Provincia',
  7: 'Córdoba',
  2: 'Salta',
};

// El scraper ya manda `loteriaClave` en cada extracto, pero los documentos
// guardados antes de esa versión no la tienen. Este mapa es el respaldo para
// que los logos no desaparezcan mientras queden documentos viejos en Firestore.
const CLAVE_LOTERIA = {
  6: 'jujena',
  1: 'ciudad',
  8: 'provincia',
  7: 'cordoba',
  2: 'salta',
};

const MS_POR_LOTERIA = 5200;

// El formato del logo no es siempre PNG: se usa el mejor que publica cada
// lotería. El panel rotativo dibuja el logo a ~500x168, y con los PNG chicos
// que había antes (Córdoba 200x87, Provincia 260x66) el navegador tenía que
// estirarlos casi al doble — se veía pixelado.
//
//   cordoba   → SVG oficial: vectorial, no se pixela nunca
//   provincia → WebP de 488x132 de su sitio, alcanza para el tamaño pintado
//
// Ciudad y Salta siguen en PNG por decisión de marca: las versiones que
// publican sus sitios son de otra marca (LOTBA y Tecno Acción) y cambiarlas
// alteraría lo que dice esa columna.
const FORMATO_LOGO = { cordoba: 'svg', provincia: 'webp' };
const rutaLogo = (clave) =>
  `${process.env.PUBLIC_URL || ''}/img/loterias/${clave}.${FORMATO_LOGO[clave] || 'png'}`;

/* ─────────────────── El link a un extracto puntual ───────────────────────
   Lo que sí llega entero a cualquier chat es un link de texto: `wa.me` no
   adjunta archivos —es un link de texto por diseño— pero un link viaja
   perfecto. Este apunta al extracto exacto que se estaba mirando —lotería,
   turno y fecha— y del otro lado abre ese mismo modal.

   La app usa HashRouter, así que el parámetro va DESPUÉS del `#`:

     https://inprojuy.gob.ar/#/?extracto=jujena-nocturna-2026-08-20

   Va en palabras y no en ids: el link llega pelado a un chat y así se lee al
   ojo qué promete antes de tocarlo.

   La base sale de la ventana y no de la constante SITIO: el link tiene que
   abrir el MISMO sitio desde el que se copió. */
const ranura = (txt) => String(txt || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

export function enlaceExtracto({ clave, turno, fechaISO }) {
  if (!clave || !turno || !fechaISO) return null;
  const base =
    typeof window !== 'undefined' && window.location
      ? `${window.location.origin}${window.location.pathname}`
      : 'https://inprojuy.gob.ar/';
  return `${base}#/?extracto=${ranura(clave)}-${ranura(turno)}-${fechaISO}`;
}

/** Lee el parámetro del hash. Devuelve { loteria, turno, fechaISO } o null. */
export function pedidoDeEnlace(hash) {
  const h = String(hash || '');
  const i = h.indexOf('?');
  if (i === -1) return null;

  let valor = null;
  try {
    valor = new URLSearchParams(h.slice(i + 1)).get('extracto');
  } catch (e) {
    return null;
  }
  // `jujena-nocturna-2026-08-20`: la lotería nunca lleva guion, el turno puede.
  const m = /^([a-z0-9]+)-(.+)-(\d{4}-\d{2}-\d{2})$/.exec(ranura(valor));
  if (!m) return null;
  return { loteria: m[1], turno: m[2], fechaISO: m[3] };
}

function buscarPedido(quiniela, pedido) {
  if (!Array.isArray(quiniela) || !pedido) return null;
  return (
    quiniela.find(
      (e) =>
        e?.numeros?.length &&
        ranura(e.loteriaClave || CLAVE_LOTERIA[e.loteriaId]) === pedido.loteria &&
        ranura(e.turno || `sorteo ${e.turnoId}`) === pedido.turno
    ) || null
  );
}

/* Para el aviso de "no está": el pedido llega en ranuras y hay que volver a
   escribirlo como lo lee una persona. */
const ID_POR_CLAVE = Object.entries(CLAVE_LOTERIA).reduce(
  (acc, [id, clave]) => ({ ...acc, [clave]: Number(id) }),
  {}
);

function pedidoEnPalabras(pedido) {
  const nombre = NOMBRE_LOTERIA[ID_POR_CLAVE[pedido.loteria]] || pedido.loteria;
  const turno = pedido.turno
    .split('-')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
  const [a, m, d] = pedido.fechaISO.split('-');
  return `${nombre} · ${turno} · ${d}/${m}/${a}`;
}

const movimientoReducido = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function agruparPorTurno(quiniela) {
  const porTurno = new Map();

  quiniela.forEach((e) => {
    if (!porTurno.has(e.turnoId)) {
      porTurno.set(e.turnoId, {
        turnoId: e.turnoId,
        turno: e.turno,
        sorteoNro: e.sorteoNro,
        porLoteria: {},
      });
    }
    porTurno.get(e.turnoId).porLoteria[e.loteriaId] = e;
  });

  // Primero los turnos conocidos en su orden real; después cualquiera nuevo
  // que aparezca, para no perderlo si el sitio agrega uno.
  const conocidos = ORDEN_TURNOS.filter((t) => porTurno.has(t));
  const resto = [...porTurno.keys()].filter((t) => !ORDEN_TURNOS.includes(t));
  return [...conocidos, ...resto].map((t) => porTurno.get(t));
}

/* ─────────────────────────── Compartir un extracto ─────────────────────────
   Lo que viaja es una IMAGEN, no un texto: un texto se edita antes de
   reenviarlo y cambiarle un número a un extracto oficial es trivial. El
   texto queda como respaldo para los navegadores que no saben mandar
   archivos (ver `Compartir.jsx`).

   DÓNDE VA EL BOTÓN, Y DÓNDE NO
   · En el modal de los 20 → sí, y es el ÚNICO lugar del tablero. Quien lo
     abrió ya encontró lo que vino a buscar; compartir es la acción que sigue,
     y lo que se manda es el extracto entero.
   · En el panel rotativo → NO. Tuvo uno al lado del CTA dorado, que mandaba
     la cabeza sola: un número sin contexto no es un extracto. Sacarlo además
     le devuelve al dorado toda la saliencia de la lámina, que es de suma
     cero, y el modal está a un clic de ahí.
   · En las 25 celdas de la tabla → NO. Es el mismo criterio que ya sacó los
     logos de las celdas: repetido 25 veces, el ícono deja de ser una acción y
     pasa a ser textura, y la tabla —que existe para SERVIR, para encontrar un
     turno rápido— se vuelve un muestrario. Además el camino ya existe: tocar
     la celda abre el modal, y ahí está el botón, con el extracto completo.
   · "Compartir el día entero" → NO. Serían 25 números sin jerarquía en una
     sola imagen; ilegible en la miniatura de un chat, que es donde se decide
     si alguien la abre.

   EL TEXTO ES EL RESPALDO, NO EL ACOMPAÑANTE
   La imagen ya lleva todo lo que hace verificable el dato —lotería, turno,
   sorteo, fecha, los 20 números, la atribución y el dominio—. Mandar además
   el texto al lado era repetirlo en el único formato que se puede editar
   antes de reenviar, y encima tapaba la vista previa del chat. Este texto se
   usa solamente cuando el navegador NO sabe mandar archivos. */
function textoExtracto({ nombre, turno, sorteoNro, fecha, numeros }) {
  const partes = [`${nombre} · ${turno}`, `Cabeza: ${numeros[0]}`, ''];

  const ident = [sorteoNro ? `Sorteo Nº ${sorteoNro}` : null, fecha]
    .filter(Boolean).join(' · ');
  if (ident) partes.push(ident);

  if (numeros.length > 1) {
    partes.push('Los 20 números, en orden de salida:');
    partes.push(numeros.map((n, i) => `${i + 1}. ${n}`).join(' · '));
  }

  partes.push('', 'Extracto oficial publicado por Lotería Jujeña.',
    'Verificalo acá:');
  return partes.join('\n');
}

function CompartirExtracto({ extracto, turno, fecha, fechaISO }) {
  const nombre = NOMBRE_LOTERIA[extracto.loteriaId] || extracto.loteria;
  const clave = extracto.loteriaClave || CLAVE_LOTERIA[extracto.loteriaId];
  const sorteoNro = extracto.sorteoNro;
  const numeros = extracto.numeros;

  // Sin `fechaISO` no hay link posible: pasa con los documentos viejos de
  // Firestore, anteriores al campo. Ahí el botón del link no se dibuja, en
  // vez de copiar un link que no abriría nada.
  const enlace = useMemo(
    () => enlaceExtracto({ clave, turno, fechaISO }) || undefined,
    [clave, turno, fechaISO],
  );

  const hacerArchivo = useCallback(
    () => imagenExtracto({
      loteria: nombre,
      logo: rutaLogo(clave),
      turno,
      sorteoNro,
      fecha,
      numeros,
      todos: true,
    }),
    [nombre, clave, turno, sorteoNro, fecha, numeros],
  );

  const que = `del extracto de ${nombre}, ${turno}${fecha ? `, ${fecha}` : ''}, con los 20 números`;

  return (
    <BotonCompartir
      hacerArchivo={hacerArchivo}
      firma={`${clave}|${turno}|${sorteoNro}`}
      titulo={`${nombre} · ${turno}`}
      texto={textoExtracto({ nombre, turno, sorteoNro, fecha, numeros })}
      aria={`Compartir la imagen ${que}`}
      ariaCopia={`Copiar la imagen ${que}`}
      enlace={enlace}
      // Una línea, no los 20 números: el dato completo vive del otro lado del
      // link, donde no se puede editar. WhatsApp además le arma vista previa.
      resumen={`${nombre} · ${turno}${sorteoNro ? ` · Sorteo Nº ${sorteoNro}` : ''}${fecha ? ` · ${fecha}` : ''}`}
      ariaEnlace={`Enviar por WhatsApp el link ${que}`}
      variante="solido"
    />
  );
}

/* ──────────────────────── Modal con los 20 números ────────────────────────
   Va por portal al body: el tablero está dentro de secciones con transform
   y position, y ahí un position:fixed se cuelga del ancestro en vez de la
   ventana. */
function ModalExtracto({ extracto, turno, fecha, fechaISO, onCerrar }) {
  const cajaRef = useRef(null);
  const cerrarRef = useRef(null);

  const nombre = NOMBRE_LOTERIA[extracto.loteriaId] || extracto.loteria;
  const clave = extracto.loteriaClave || CLAVE_LOTERIA[extracto.loteriaId];

  useEffect(() => {
    cerrarRef.current?.focus();

    // El fondo no se scrollea mientras el modal está abierto. Se guarda el
    // valor previo en vez de asumir que era "", porque el menú móvil de la
    // home también toca esto.
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const alTeclear = (e) => {
      if (e.key === 'Escape') {
        onCerrar();
        return;
      }
      if (e.key !== 'Tab') return;

      // Trampa de foco: sin esto el tabulador se va a la página de atrás,
      // que para el lector de pantalla sigue estando "abierta".
      const focusables = cajaRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables?.length) return;
      const primero = focusables[0];
      const ultimo = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === primero) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault();
        primero.focus();
      }
    };

    document.addEventListener('keydown', alTeclear);
    return () => {
      document.removeEventListener('keydown', alTeclear);
      document.body.style.overflow = overflowPrevio;
    };
  }, [onCerrar]);

  return createPortal(
    <div className="te-modal-fondo" onClick={onCerrar} role="presentation">
      <div
        className="te-modal"
        ref={cajaRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="te-modal-titulo"
        // El clic de adentro no tiene que cerrar: solo el del fondo.
        onClick={(e) => e.stopPropagation()}
      >
        <div className="te-modal-cab">
          <div className="te-modal-ident">
            <span className="te-logo te-logo-md">
              <img src={rutaLogo(clave)} alt="" aria-hidden="true" />
            </span>
            <div>
              <h4 id="te-modal-titulo">{nombre}</h4>
              <p>
                {turno}
                {extracto.sorteoNro ? ` · Sorteo Nº ${extracto.sorteoNro}` : ''}
                {fecha ? ` · ${fecha}` : ''}
              </p>
            </div>
          </div>

          <button
            type="button"
            className="te-modal-cerrar"
            onClick={onCerrar}
            ref={cerrarRef}
            aria-label="Cerrar los resultados"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        {/* Clase propia y no la .hc-tile de la home: esa pinta el número con
            var(--cream) sin valor de respaldo, y como el modal sale por portal
            al body queda fuera de .hc-page, donde esa variable no existe. El
            número terminaba heredando un color oscuro, ilegible sobre el
            fondo del modal. */}
        <ol className="te-veinte">
          {extracto.numeros.map((n, i) => (
            <li key={`${i}-${n}`}>
              <span className="te-pos">{i + 1}</span>
              <span className="te-num">{n}</span>
            </li>
          ))}
        </ol>

        {/* El botón va acá abajo y no en la cabecera: arriba compite con el
            de cerrar, y el pie es donde termina de leerse el extracto. */}
        <div className="te-modal-cierre">
          <CompartirExtracto
            extracto={extracto}
            turno={turno}
            fecha={fecha}
            fechaISO={fechaISO}
          />
          <p className="te-modal-pie">
            Resultados oficiales publicados por Lotería Jujeña.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ─────────────────────────── El panel que rota ───────────────────────────
   Las cinco loterías del último turno, una por vez. Todas las láminas viven
   en la misma celda de grilla y se cruzan por opacidad: así el alto lo pone
   la más alta y no hace falta fijarlo a mano. */
function PanelRotativo({ turno, onVerNumeros }) {
  const conDatos = useMemo(
    () => ORDEN_LOTERIAS.filter((lid) => turno.porLoteria[lid]?.numeros?.length),
    [turno]
  );

  const [indice, setIndice] = useState(0);
  const [pausado, setPausado] = useState(false);

  // El tablero va en claro: es contenido numérico y denso, y sobre fondo oscuro
  // el texto chico se lee peor (pupila 3,65 mm contra 2,09; evidencia nivel A).
  // Además, sobre oscuro los logos necesitaban una pastilla clara que terminaba
  // siendo el objeto más brillante del tablero, compitiendo con el número.
  //
  // La clase va en <body> y no acá: el modal de los 20 números sale por portal,
  // así que desde este árbol no lo alcanzaría (es la trampa 4 documentada en
  // docs/frontend/home-comercial-extractos.md).
  useEffect(() => {
    document.body.classList.add('te-claro');
    return () => { document.body.classList.remove('te-claro'); };
  }, []);

  // Si cambia el turno (llegó un sorteo nuevo), la rotación vuelve a empezar
  // por la de casa en vez de quedar apuntando a una posición que ya no existe.
  useEffect(() => { setIndice(0); }, [turno.turnoId]);

  useEffect(() => {
    if (pausado || conDatos.length < 2 || movimientoReducido()) return undefined;
    const id = setInterval(
      () => setIndice((i) => (i + 1) % conDatos.length),
      MS_POR_LOTERIA
    );
    return () => clearInterval(id);
  }, [pausado, conDatos.length]);

  if (!conDatos.length) return null;
  const activa = conDatos[Math.min(indice, conDatos.length - 1)];

  return (
    <div
      className="te-panel"
      onMouseEnter={() => setPausado(true)}
      onMouseLeave={() => setPausado(false)}
      onFocusCapture={() => setPausado(true)}
      onBlurCapture={() => setPausado(false)}
      // En pantalla táctil no hay hover: si sigue rotando, el dedo llega
      // cuando ya cambió la lotería y se abre el extracto equivocado. Al
      // primer toque la rotación se detiene y maneja la persona.
      onTouchStart={() => setPausado(true)}
    >
      <div className="te-panel-laminas">
        {conDatos.map((lid) => {
          const e = turno.porLoteria[lid];
          const esActiva = lid === activa;
          const clave = e.loteriaClave || CLAVE_LOTERIA[lid];

          return (
            <div
              className={'te-lamina' + (esActiva ? ' activa' : '')}
              key={lid}
              aria-hidden={!esActiva}
            >
              {/* Dos columnas del mismo ancho: la marca a la izquierda, el
                  resultado a la derecha. El logo ocupa toda su mitad porque
                  es lo que se vende; el número es lo que se viene a buscar. */}
              <span className="te-logo te-logo-lg">
                <img src={rutaLogo(clave)} alt={NOMBRE_LOTERIA[lid]} />
              </span>

              <div className="te-lamina-datos">
                <p className="te-lamina-turno">
                  {turno.turno || `Sorteo ${turno.turnoId}`}
                  {turno.sorteoNro ? <> · Sorteo Nº {turno.sorteoNro}</> : null}
                </p>

                <p className="te-lamina-rotulo">Cabeza · 1er premio</p>
                <strong className="te-lamina-numero">{e.numeros[0]}</strong>

                {/* Un solo botón, y a propósito: compartir la cabeza suelta
                    es compartir un número sin contexto. El extracto que vale
                    la pena mandar son los 20, y está del otro lado de este
                    mismo botón. */}
                <button
                  type="button"
                  className="te-lamina-cta"
                  onClick={() => onVerNumeros(turno.turnoId, lid)}
                  // Las láminas de atrás siguen en el DOM para que el cruce
                  // de opacidad funcione, pero no tienen que recibir el
                  // tabulador.
                  tabIndex={esActiva ? 0 : -1}
                >
                  Ver los 20 números <span aria-hidden="true">→</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {conDatos.length > 1 && (
        <div className="te-puntos" role="tablist" aria-label="Elegir lotería">
          {conDatos.map((lid) => (
            <button
              type="button"
              key={lid}
              role="tab"
              aria-selected={lid === activa}
              aria-label={NOMBRE_LOTERIA[lid]}
              className={'te-punto' + (lid === activa ? ' activo' : '')}
              onClick={() => setIndice(conDatos.indexOf(lid))}
            >
              <img
                src={rutaLogo(turno.porLoteria[lid].loteriaClave || CLAVE_LOTERIA[lid])}
                alt=""
                aria-hidden="true"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── La tabla de todo el día ─────────────────────── */
function TablaDelDia({ turnos, onVerNumeros }) {
  return (
    <div className="te-tabla-caja">
      <table className="te-tabla">
        <caption className="te-tabla-titulo">
          Todos los turnos del día. Tocá un número para ver los 20.
        </caption>
        <thead>
          <tr>
            <th scope="col" className="te-th-turno">Turno</th>
            {ORDEN_LOTERIAS.map((lid) => (
              <th scope="col" key={lid}>
                <span className="te-logo te-logo-sm">
                  <img src={rutaLogo(CLAVE_LOTERIA[lid])} alt={NOMBRE_LOTERIA[lid]} />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {turnos.map((t) => (
            <tr key={t.turnoId}>
              <th scope="row" className="te-th-turno">
                <strong>{t.turno || `Sorteo ${t.turnoId}`}</strong>
                {t.sorteoNro && <span>Nº {t.sorteoNro}</span>}
              </th>

              {ORDEN_LOTERIAS.map((lid) => {
                const e = t.porLoteria[lid];

                // La Previa no tiene Salta: el hueco es real y se muestra.
                // Si se escondiera la celda, parecería que ese sorteo tuvo
                // cuatro loterías en vez de que a una le falta.
                if (!e?.numeros?.length) {
                  return (
                    <td key={lid} className="te-td vacia">
                      <span aria-label="sin sorteo">—</span>
                    </td>
                  );
                }

                return (
                  <td key={lid} className={'te-td' + (lid === LOTERIA_PROPIA ? ' propia' : '')}>
                    <button
                      type="button"
                      onClick={() => onVerNumeros(t.turnoId, lid)}
                      aria-label={`${NOMBRE_CORTO[lid]}, ${t.turno}: cabeza ${e.numeros[0]}. Ver los 20 números`}
                    >
                      {e.numeros[0]}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function TableroExtractos({ datos }) {
  const [abierto, setAbierto] = useState(null); // { turnoId, loteriaId }
  const disparadorRef = useRef(null);           // elemento que abrió el modal

  const abrir = useCallback((turnoId, loteriaId) => {
    disparadorRef.current = document.activeElement;
    setAbierto({ turnoId, loteriaId });
  }, []);

  const cerrar = useCallback(() => {
    setAbierto(null);
    // El foco vuelve a donde estaba: si no, queda en el body y el tabulador
    // arranca de nuevo desde arriba de la página.
    disparadorRef.current?.focus?.();
  }, []);

  const turnos = useMemo(
    () => (datos?.quiniela?.length ? agruparPorTurno(datos.quiniela) : []),
    [datos]
  );

  /* ── El link compartido ────────────────────────────────────────────────
     El parámetro se lee una sola vez, al montar, pero RESOLVERLO depende de
     que hayan llegado los datos: el efecto cuelga de `datos` y no de []. Es
     la trampa 1 de este repo —el observador que se registra al montar y
     nunca ve lo que llegó después de Firestore—, con otra ropa. */
  const [pedido] = useState(() =>
    (typeof window !== 'undefined' ? pedidoDeEnlace(window.location.hash) : null));
  const [enlazado, setEnlazado] = useState(null);
  const [avisoEnlace, setAvisoEnlace] = useState(null);
  const resuelto = useRef(false);
  const tableroRef = useRef(null);

  useEffect(() => {
    // Se resuelve una sola vez. Y el resultado NO se descarta en la limpieza:
    // con StrictMode en desarrollo el efecto se monta, se limpia y se vuelve a
    // montar, y abortar ahí dejaba el link sin abrir nada y sin avisar nada.
    // En React 18, un setState después de desmontar es inocuo.
    if (!pedido || resuelto.current || !datos) return;
    resuelto.current = true;

    const abrirCon = (dia) => {
      const e = buscarPedido(dia?.quiniela, pedido);
      if (!e) {
        setAvisoEnlace(pedido);
        return;
      }
      setEnlazado({
        extracto: e,
        turno: e.turno || `Sorteo ${e.turnoId}`,
        fecha: dia.fecha,
        fechaISO: dia.fechaISO || pedido.fechaISO,
      });
    };

    // Lo más común: el link es del día que ya está en pantalla.
    if (datos.fechaISO === pedido.fechaISO) {
      abrirCon(datos);
      return;
    }

    // Un link de otro día. Cada día es su propio documento, así que uno de la
    // semana pasada sigue abriendo lo que prometía. Si esa fecha no está
    // publicada, la página no rompe: muestra el día de hoy y lo dice.
    (async () => {
      const dia = await extractosDeFecha(pedido.fechaISO);
      if (!dia) {
        setAvisoEnlace(pedido);
        return;
      }
      abrirCon(dia);
    })();
  }, [datos, pedido]);

  /* Si el link no llevaba a ningún lado, el aviso tiene que VERSE: quien
     abrió ese link cayó arriba de todo, y el tablero está mil píxeles abajo.
     Se sube la página hasta el tablero, dejando lugar para la barra fija. */
  useEffect(() => {
    if (!avisoEnlace || !tableroRef.current) return;
    const caja = tableroRef.current.getBoundingClientRect();
    window.scrollTo({ top: Math.max(0, caja.top + window.scrollY - 96) });
  }, [avisoEnlace]);

  const cerrarEnlazado = useCallback(() => {
    setEnlazado(null);
    // Se llegó por un link: no hay elemento que haya abierto el modal, así que
    // el foco va al tablero, que es lo que queda debajo.
    tableroRef.current?.focus?.();
    tableroRef.current?.scrollIntoView?.({ block: 'start' });
  }, []);

  if (!turnos.length) return null;

  // El panel muestra el sorteo más reciente: `turnos` ya viene en el orden
  // real del día, así que el último del arreglo es el que acaba de salir.
  const ultimoTurno = turnos[turnos.length - 1];

  const turnoAbierto = abierto && turnos.find((t) => t.turnoId === abierto.turnoId);
  const extractoAbierto = turnoAbierto?.porLoteria[abierto.loteriaId];

  return (
    <div className="hc-board hc-reveal dly-1" ref={tableroRef} tabIndex={-1}>
      <div className="hc-board-bar">
        <h3>
          Sorteos del día
          {datos.fecha ? <> · <b>{datos.fecha}</b></> : null}
        </h3>
        <span className="hc-live-dot">Actualizado</span>
      </div>

      {avisoEnlace && (
        <p className="te-aviso-enlace" role="status">
          <strong>{pedidoEnPalabras(avisoEnlace)}</strong> no está publicado.
          Estos son los últimos extractos que sí.
        </p>
      )}

      <PanelRotativo turno={ultimoTurno} onVerNumeros={abrir} />

      <TablaDelDia turnos={turnos} onVerNumeros={abrir} />

      {/* El id es un ancla del menú principal (#donde-jugar): tiene que
          existir sí o sí, esté o no cargado el extracto. */}
      <div className="hc-board-foot">
        <a href="#resultados" id="donde-jugar">
          Ver todos los extractos <span aria-hidden="true">→</span>
        </a>
      </div>

      {/* Uno u otro, nunca los dos: el del link se abre al cargar, cuando
          todavía no hubo ningún clic en el tablero. */}
      {enlazado ? (
        <ModalExtracto
          extracto={enlazado.extracto}
          turno={enlazado.turno}
          fecha={enlazado.fecha}
          fechaISO={enlazado.fechaISO}
          onCerrar={cerrarEnlazado}
        />
      ) : extractoAbierto ? (
        <ModalExtracto
          extracto={extractoAbierto}
          turno={turnoAbierto.turno || `Sorteo ${turnoAbierto.turnoId}`}
          fecha={datos.fecha}
          fechaISO={datos.fechaISO}
          onCerrar={cerrar}
        />
      ) : null}
    </div>
  );
}
